/**
 * Initialization service for managing app and farm initialization.
 *
 * This service provides async functions for initialization.
 * Components can call these functions directly instead of using dispatch.
 *
 * Note: This module uses React Query for data fetching and Zustand for state.
 */

import { queryClient } from "common/queryClient";
import type { FeatureCollection } from "geojson";
import { AnimalGroup, Farm } from "model";
import { useAppStore, useUIStore } from "stores";
import { fetchToken as fetchTokenService } from "./auth";
import {
  fetchAnimalGroupsByIdRQ,
  fetchFarmByIdRQ,
  finishLoadingStateRQ,
  initializeAppRQ,
  initializeFarmRQ,
  switchFarmRQ,
} from "./initializationRQ";

/**
 * Initialize the application after login.
 * Validates the JWT token, fetches user data, and sets up the app state.
 * Uses React Query for data fetching and Zustand for state.
 *
 * @param options - Optional configuration
 * @param options.skipLoadingStateReset - If true, don't set loading to idle at the end
 *   (useful when farm initialization will follow immediately)
 * @returns The result containing farms, user data, and whether farm init is needed
 */
export async function initializeApp(options?: {
  skipLoadingStateReset?: boolean;
}): Promise<{
  farms?: Farm[];
  user?: import("model").User;
  needsFarmInit?: boolean;
}> {
  if (initializeAppPromise) {
    return await initializeAppPromise;
  }

  initializeAppPromise = initializeAppRQ(options).finally(() => {
    initializeAppPromise = null;
  });

  return await initializeAppPromise;
}

/**
 * Initialize a specific farm.
 * Fetches all farm data including fields, animal groups, stock classes, etc.
 * Uses React Query for data fetching and caching.
 *
 * @param farm - The farm to initialize
 * @param options - Optional configuration
 * @param options.force - If true, re-initialize even if same farm is already loaded
 */
export async function initializeFarm(
  farm: Farm,
  options?: { force?: boolean }
): Promise<void> {
  const { force = false } = options ?? {};

  // During app load multiple callers can race to initialize the same farm
  // (route init, legacy screens, components mounting). Dedup in-flight work.
  if (!force) {
    const existing = initializeFarmPromises.get(farm.guid);
    if (existing) {
      return await existing;
    }
  }

  const promise = initializeFarmRQ(farm, options).finally(() => {
    // Only clear if this promise is still the active one for the farm.
    if (initializeFarmPromises.get(farm.guid) === promise) {
      initializeFarmPromises.delete(farm.guid);
    }
  });

  if (!force) {
    initializeFarmPromises.set(farm.guid, promise);
  }

  return await promise;
}

/**
 * Switch to a different farm.
 * Fetches the farm and initializes it.
 * Uses React Query for data fetching.
 *
 * @param farmId - The ID of the farm to switch to
 */
export async function switchFarm(farmId: string): Promise<void> {
  await switchFarmRQ(farmId);
}

/**
 * Mark loading as finished.
 * Updates Zustand state.
 */
export function finishLoadingState(): void {
  finishLoadingStateRQ();
}

/**
 * Set the application locale.
 * Uses Zustand store.
 *
 * @param locale - The locale to set (e.g., 'en', 'de')
 */
export function setLocale(locale: string): void {
  useAppStore.getState().setLocale(locale);
}

/**
 * Set the map to zoom to a specific geometry.
 * Uses Zustand store.
 *
 * @param geom - The feature collection to zoom to
 */
export function zoomToGeom(geom: FeatureCollection | null): void {
  useUIStore.getState().setZoomToGeom(geom);
}

/**
 * Reset all stores (used for logout).
 * Resets Zustand stores and React Query cache.
 */
export function resetStore(): void {
  // Reset Zustand stores
  useAppStore.getState().reset();
  useUIStore.getState().reset();
  // Clear React Query cache
  queryClient.clear();
  initializeAppPromise = null;
  initializeFarmPromises.clear();
}

/**
 * Fetch a farm by its ID.
 * Uses React Query for data fetching.
 *
 * @param farmId - The ID of the farm to fetch
 * @returns The fetched farm
 */
export async function fetchFarmById(farmId: string): Promise<Farm> {
  return await fetchFarmByIdRQ(farmId);
}

/**
 * Fetch animal groups by their IDs.
 * Uses React Query for data fetching.
 *
 * @param animalGroupIds - Array of animal group IDs to fetch
 * @returns The fetched animal groups
 */
export async function fetchAnimalGroupsById(
  animalGroupIds: string[]
): Promise<AnimalGroup[]> {
  return await fetchAnimalGroupsByIdRQ(animalGroupIds);
}

/**
 * Fetch token and store it in localStorage.
 * Uses direct API call.
 *
 * @param credentials - Email and password
 * @returns Token response
 */
export async function fetchTokenAndStore(credentials: {
  mail: string;
  password: string;
}): Promise<{ token: string }> {
  const result = await fetchTokenService(
    credentials.mail,
    credentials.password
  );
  // Store the token in localStorage (stores for subsequent requests)
  localStorage.setItem("jwt", result.token);
  return result;
}

let initializeAppPromise: Promise<{
  farms?: Farm[];
  user?: import("model").User;
  needsFarmInit?: boolean;
}> | null = null;

const initializeFarmPromises: Map<string, Promise<void>> = new Map();
